import axios from "axios";
import { useQuery } from "react-query";
import { Config } from "./../config";

const leaveMicroserviceBaseUrl = Config.serviceUrls.leaveBaseUrl;
const personnelBaseUrl = Config.serviceUrls.personnelBaseUrl;

export async function getLeaveTypes() {
  const response = await axios.get(
    `${leaveMicroserviceBaseUrl}/Leave/LeaveType`,
  );
  return response.data;
}

export function useLeaveTypes() {
  return useQuery(["leaveTypes"], getLeaveTypes);
}

export async function getLeaveDetail(staffId, invitationId) {
  const response = await axios.get(`${leaveMicroserviceBaseUrl}/Leave/Detail`, {
    params: {
      staffId,
      invitationId,
    },
  });

  return response.data;
}
export async function getLeaveDetailWithLeaveId(paramsId) {
  const response = await axios.get(
    `${leaveMicroserviceBaseUrl}/Leave/${paramsId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}
export const updateLeaveStatus = async (params) => {
  let response = await axios.put(
    `${leaveMicroserviceBaseUrl}/Leave/Action`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};

const getStaffLeaveListing = async ({
  officeId,
  userId,
  year,
  pageNumber,
  pageSize,
  sortByFromDateAscending,
  sortByToDateAscending,
  selectedLeaveStatuses = [],
}) => {
  const params = new URLSearchParams({
    officeId,
    userid: userId,
    year,
    pageNo: pageNumber,
    pageSize,
    sortByFromDateAscending,
    sortByToDateAscending,
  });

  // Append each selected leave status as a separate 'status' parameter
  selectedLeaveStatuses.forEach((status) => {
    params.append("status", status);
  });

  const response = await axios.get(
    `${leaveMicroserviceBaseUrl}/Leave?${params.toString()}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};

export const useStaffLeaveListing = (
  officeId,
  userId,
  year,
  pageNumber,
  pageSize,
  selectedLeaveStatuses,
  sortByFromDateAscending,
  sortByToDateAscending,
  options = {},
) => {
  return useQuery(
    [
      "staff-leave-listing",
      officeId,
      userId,
      year,
      pageNumber,
      pageSize,
      selectedLeaveStatuses,
      sortByFromDateAscending,
      sortByToDateAscending,
    ],
    () =>
      getStaffLeaveListing({
        officeId,
        userId,
        year,
        pageNumber,
        pageSize,
        selectedLeaveStatuses,
        sortByFromDateAscending,
        sortByToDateAscending,
      }),
    options,
  );
};

const getLeaveListing = async ({
  officeId,
  statusId,
  year,
  pageNumber,
  pageSize,
  searchTerm,
  selectedLeaveStatuses = [],
  selectedOwnerId,
}) => {
  const params = new URLSearchParams({
    officeId,
    year,
    pageNo: pageNumber,
    pageSize,
    searchTerm,
    selectedOwnerId,
    ownerId: selectedOwnerId,
  });

  selectedLeaveStatuses.forEach((status) => {
    params.append("status", status);
  });

  const response = await axios.get(
    `${leaveMicroserviceBaseUrl}/Leave/Office?${params.toString()}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};

export const useLeaveListing = (
  officeId,
  statusId,
  year,
  pageNumber,
  pageSize,
  searchTerm,
  selectedLeaveStatuses,
  selectedOwnerId,
  options = {},
) => {
  return useQuery(
    [
      "leave-listing",
      officeId,
      statusId,
      year,
      pageNumber,
      pageSize,
      searchTerm,
      selectedLeaveStatuses,
      selectedOwnerId,
    ],
    () =>
      getLeaveListing({
        officeId,
        statusId,
        year,
        pageNumber,
        pageSize,
        searchTerm,
        selectedLeaveStatuses,
        selectedOwnerId,
      }),
    options,
  );
};

async function getTemporaryStaffList(
  officeId,
  LeaveId,
  fromDate,
  toDate,
  userId,
) {
  const response = await axios.post(`${personnelBaseUrl}/Resume/InternalJob`, {
    officeId,
    LeaveId,
    fromDate,
    toDate,
    userId,
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return { data: response.data.data, pagination: response.data.pagination };
}

export function useTemporaryStaffList(
  officeId,
  LeaveId,
  fromDate,
  toDate,
  userId,
) {
  return useQuery(
    ["staff-temporary-list", officeId, LeaveId, fromDate, toDate, userId],
    () => getTemporaryStaffList(officeId, LeaveId, fromDate, toDate, userId),
  );
}

export const addBackupStaff = async (params) => {
  let response = await axios.patch(
    `${leaveMicroserviceBaseUrl}/Leave/UpdateBackup`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};

const getLeaveCategoryCounts = async ({ officeId, userId }) => {
  const response = await axios.get(
    `${leaveMicroserviceBaseUrl}/Leave/Detail?officeId=${officeId}&userId=${userId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};

export const useLeaveCategoryCounts = (officeId, userId, options = {}) => {
  return useQuery(
    ["leave-category-counts", officeId, userId],
    () => getLeaveCategoryCounts({ officeId, userId }),
    options,
  );
};

export const applyLeave = async (params) => {
  const response = await axios.post(
    `${leaveMicroserviceBaseUrl}/Leave`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};

export const archiveLeave = async (params) => {
  const response = await axios.post(
    `${leaveMicroserviceBaseUrl}/Leave/ArchiveStatusUpdate`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};

export const cancelLeave = async (params) => {
  const response = await axios.put(
    `${leaveMicroserviceBaseUrl}/Leave/CancelLeave`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};

const getLeaveHisotryLogs = async ({ leaveId }) => {
  const response = await axios.get(
    `${leaveMicroserviceBaseUrl}/Leave/LeaveHistory?LeaveId=${leaveId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
};

export const useLeaveHistoryLogs = (leaveId, options = {}) => {
  return useQuery(
    ["Leave/LeaveHistory", leaveId],
    () => getLeaveHisotryLogs({ leaveId }),
    options,
  );
};
